import React, { useState, useEffect, useRef } from 'react';
import InnerHTML from '@Components/InnerHTML';
import ShowMore from '@Components/Results/utils/ShowMore';
import * as Spinners from '@Services/Spinners';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import Tooltip from '@Components/Tooltip';
import NotificationBar from '@Components/NotificationBar';
import StickyTableHeaderComponent from '@Components/Results/sticky-table-header.component';
import { dispatchStoreProducts, dispatchGetProductsFromStore } from '@Containers/RcsResults/RcsResultsActions';
import RcsCheckboxNew from '@Pages/Results/Containers/RcsCheckboxContainer';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import size from 'lodash/size';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import { HEATMAP_CELL_DETAILS_DIALOG_CONTENT_CLASS_NAME } from '@Pages/Heatmap/HeatmapCellDetailsDialog/constants';
import { SubstanceCommercialSupplierInformationCellAvailability } from './SubstanceCommercialSupplierInfoCellAvailability';
import { SubstanceCommercialSupplierInformationCellPrice } from './SubstanceCommercialSupplierInfoCellPrice';
import { SubstanceCommercialSupplierInformationCellPurity } from './SubstanceCommercialSupplierInfoCellPurity';
import { SubstanceCommercialSupplierInformationCellProduct } from './SubstanceCommercialSupplierInfoCellProduct';
import { SubstanceCommercialSupplierInformationCellSupplier } from './SubstanceCommercialSupplierInfoCellSupplier';

import './SubstanceCommercialSupplierInfo.scss';

function SubstanceCommercialSupplierInformation(props) {
  const {
    data: initialData,
    uniqueId,
    metadata,
    total,
    initialItemsAmount,
    details,
    substanceId,
    substanceIndex,
    retrieveData,
    track,
  } = props;
  const [data, setData] = useState(initialData);
  const [isShowLess, setIsShowLess] = useState(false);
  const cacheRef = useRef(null);
  const id = `${uniqueId}-commercialSuppliers`;
  const isProdSelectionEnabled = Auth.isFeatureEnabled(FEATURES.enableProductSelection);
  const isRcsExport = Auth.getUser()?.details?.databases?.[metadata.databaseId]?.name === 'Commercial Substances';

  useEffect(() => {
    Tooltip.rebuild();
    if (isRcsExport && isProdSelectionEnabled) {
      dispatchStoreProducts({ substanceId, data });
    }
  }, [isRcsExport, isProdSelectionEnabled, substanceId, data]);

  const appendRows = (rows) => {
    setData((prevData) => prevData?.concat(rows));
  };

  const showMore = (firstItem = 1, lastItem = initialItemsAmount) =>
    new Promise((resolve, reject) => {
      if (cacheRef.current) {
        appendRows(cacheRef.current);
        cacheRef.current = null;
        return resolve();
      }

      Spinners.activate(id);

      retrieveData(firstItem, lastItem)
        .then(({ commercialData }) => {
          const products = dispatchGetProductsFromStore();
          if (products?.[substanceId] && size(products[substanceId]) !== lastItem) {
            const newData = data?.concat(commercialData);
            Object.values(products[substanceId])?.forEach((prod) => {
              const productItem = find(newData, (product) => product.rcSubstanceId === prod.rcSubstanceId);
              if (productItem) productItem.checked = prod.checked;
            });
            dispatchStoreProducts({ substanceId, data: newData });
          }
          appendRows(commercialData);
          track?.(commercialData, 'showMoreCommercialData', null, firstItem, lastItem);
          return commercialData;
        })
        .catch(() => reject(new Error('No commercial suppliers data found')))
        .finally(() => {
          Tooltip.rebuild();
          Spinners.terminate(id);
          return resolve();
        });
    });

  const showLess = () => {
    setData((prevData) => {
      const newData = [...prevData];
      cacheRef.current = newData.splice(initialItemsAmount);
      return newData;
    });
  };

  const scrollContainerSelector = metadata.heatmapMode ? `.${HEATMAP_CELL_DETAILS_DIALOG_CONTENT_CLASS_NAME}` : null;
  const { casRegistryNumbers } = details;
  const handledCasRegistryNumbers = isArray(casRegistryNumbers) ? casRegistryNumbers.join(', ') : casRegistryNumbers;

  const headers = [];
  if (isProdSelectionEnabled && isRcsExport) {
    headers.push({
      id: 'selected',
      cssid: 'checkbox',
      order: 0,
      hasContent: false,
      isVisible: true,
    });
  }
  headers.push(
    { id: 'supplier', title: 'Commercial Suppliers', cssid: 'supplier', order: 1, hasContent: true, isVisible: true },
    { id: 'product', cssid: 'product', title: 'Product', order: 2, hasContent: true, isVisible: true },
    { id: 'purity', cssid: 'purity', title: 'Purity', order: 3, hasContent: true, isVisible: true },
    { id: 'size-price', cssid: 'size-price', title: 'Package size & price', colspan: '2', order: 4, hasContent: true, isVisible: true },
    { id: 'availability', cssid: 'availability', title: 'Availability', order: 5, hasContent: true, isVisible: true }
  );

  return (
    <div id={id} className="substance-results-commercial-information u-els-font-size-base u-els-line-height-default" aria-expanded>
      <table className="rx-table rx-table--full-width sticky-table-header">
        <StickyTableHeaderComponent headers={headers} staticColumns="true">
          <NotificationBar
            id="rcs-price-notify"
            type="info"
            parentType="table"
            content="Please take a moment to check individual supplier websites for the most up-to-date information on pricing and availability"
          />
        </StickyTableHeaderComponent>
        <tbody>
          {data?.map((item) => (
            <tr key={item.rcSubstanceId}>
              {isProdSelectionEnabled ? (
                <td>
                  <RcsCheckboxNew
                    substanceId={substanceId}
                    substanceIndex={substanceIndex}
                    productId={item.rcSubstanceId}
                    productIndex={item.index}
                  />
                </td>
              ) : null}
              <SubstanceCommercialSupplierInformationCellSupplier
                {...item}
                databaseId={metadata.databaseId}
                casRegistryNumbers={handledCasRegistryNumbers}
              />
              <SubstanceCommercialSupplierInformationCellProduct {...item} casRegistryNumbers={handledCasRegistryNumbers} />
              <SubstanceCommercialSupplierInformationCellPurity {...item} />
              <InnerHTML tag="td" className="column-size" html={item.packageSize} />
              <SubstanceCommercialSupplierInformationCellPrice {...item} />
              <SubstanceCommercialSupplierInformationCellAvailability {...item} />
            </tr>
          ))}
        </tbody>
      </table>

      <RxSpinnerComponent spinnerId={id} position="relative" />

      <div className={`rx-btn-group--center ${isShowLess ? 'show-less-sticky' : ''}`}>
        <ShowMore
          initial={initialItemsAmount}
          scrollContainerSelector={scrollContainerSelector}
          metadata={metadata}
          current={data?.length}
          total={total}
          container={`#${id}`}
          moreHandler={({ firstItem, lastItem }) => {
            setIsShowLess(true);
            return showMore(firstItem, lastItem);
          }}
          lessHandler={() => {
            setIsShowLess(false);
            return showLess();
          }}
        />
      </div>
    </div>
  );
}

export default SubstanceCommercialSupplierInformation;
